import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {getTemplateList, updatePageAndGetList, updateSortAndGetList} from "../../actions/templateListActions";
import {openDrawer} from "../../actions/drawerActions";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import TagCreateEditDrawerHook, {tagCreateEditDrawerProps} from "../drawers/TagCreateEditDrawerHook";
import TagReviewDrawerHook, {tagReviewDrawerProps} from "../drawers/TagReviewDrawerHook";
import ArgoTemplateListCreateFabHook from "../common/ArgoTemplateListCreateFabHook";
import ArgoScreenSizePopUpHook from "../common/ArgoScreenSizePopUpHook";
import formatCopyText from "../../utilities/formatCopyText";
import {QD_URL} from "../../constants/systemConstants";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import * as metadataTemplateTypes from "../../constants/metadataTemplateTypes";


function TagListHook({ match, location }) {
    // State hooks
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const [closePopUp, setClosePopUp] = useState(false);

    // Redux hooks
    const dispatch = useDispatch();
    const tagList = useSelector(state => state.tagList);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER.all.tag] || {});

    // Dispatch wrapper functions
    const dispatchGetTemplateList = useCallback((filter, type, mode) =>
        dispatch(getTemplateList(filter, type, mode)), [dispatch]);
    const dispatchUpdatePageAndGetList = useCallback((page, size, filter, type, mode, event) => 
        dispatch(updatePageAndGetList(page, size, filter, type, mode, event)), [dispatch]);
    const dispatchUpdateSortAndGetList = useCallback((sort, direction, filter, type, mode) => 
        dispatch(updateSortAndGetList(sort, direction, filter, type, mode)), [dispatch]);
    const dispatchOpenDrawer = useCallback((props, component) => 
        dispatch(openDrawer(props, component)), [dispatch]);

    useEffect(() => {
        setLoading(true);
        dispatchGetTemplateList(filterState, TEMPLATE.TYPE.TAG, TEMPLATE.MODE.ALL);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty deps - only run once

    useEffect(() => {
        // Called twice, first pass set filter false, then on second call list is loaded and loading animation can be stopped
        if (filter) {
            setFilter(false);
        } else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            } else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, tagList]);

// Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const setLoadingStateTrue = (callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        if (parm1 === null) {
            setLoading(true);
            setFilter(false);
            callback();
        } else {
            setLoading(true);
            setFilter(false);
            callback(parm1);
        }
    };

    // Handlers for opening drawers
    const openCreate = useCallback(() => {
        dispatchOpenDrawer(
            tagCreateEditDrawerProps, 
            <TagCreateEditDrawerHook createMode={true} setLoadingState={setLoadingStateTrue}/>
        );
    }, [dispatchOpenDrawer, setLoadingStateTrue]);

    const openEdit = useCallback((templateKey, id) => {
        dispatchOpenDrawer(
            tagCreateEditDrawerProps, 
            <TagCreateEditDrawerHook id={id} filter={filterState} setLoadingState={setLoadingStateTrue}/>
        );
    }, [dispatchOpenDrawer, filterState, setLoadingStateTrue]);

    const openPreview = useCallback((templateKey, id, name, row, event) => {
        formatCopyText(row, event, "Tag:");
        dispatchOpenDrawer(tagReviewDrawerProps, <TagReviewDrawerHook id={id}/>);
    }, [dispatchOpenDrawer]);

    const onPageChange = useCallback((page, size, event) => {
        // Only update if page/size actually changed to prevent loops
        if (page !== filterState.pageNumber || size !== filterState.pageSize) {
            setLoading(true);
            setFilter(true);
            // Use local values instead of filterState to prevent loops
            const currentFilter = {...filterState};
            dispatchUpdatePageAndGetList(page, size, currentFilter, TEMPLATE.TYPE.TAG, TEMPLATE.MODE.ALL, event);
        }
    }, [dispatchUpdatePageAndGetList, filterState]);

    const onSortChange = useCallback((sort, direction) => {
        // Only update if sort actually changed to prevent loops
        if (sort !== filterState.name || direction !== filterState.direction) {
            setLoading(true);
            setFilter(true);
            // Use local values instead of filterState to prevent loops
            const currentFilter = {...filterState};
            dispatchUpdateSortAndGetList(sort, direction, currentFilter, TEMPLATE.TYPE.TAG, TEMPLATE.MODE.ALL);
        }
    }, [dispatchUpdateSortAndGetList, filterState]);

    const handleClosePopUp = useCallback(() => {
        localStorage.setItem("hasSeenWelcomeMessage", "true");
        setClosePopUp(true);
    }, []);

    // Custom actions for table
    const customActions = [
        {
            func: openPreview,
            label: "Review"
        },
        {
            func: openEdit,
            label: "Edit"
        }
    ];

    return (
        <div id="TagList-container-div" style={{height: "100%", padding: "15px", marginTop: '64px'}}>
            {!closePopUp && <ArgoScreenSizePopUpHook closeFunction={handleClosePopUp}/>}

            <ArgoListTitleAndLoadingAnimationHook 
                fileName="TagList" 
                listTitle="Tags" 
                loading={loading}
                showOutstandingAlert={false}
                filter={filter}
            />

            <div style={{clear: "both"}}></div>

            <Card id="TagList-card"
                  variant="outlined"
                  sx={{border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke"
                  }}>
                <CardContent id="TagList-card-content">

                    <TableViewHook
                        id="TagList-table-view"
                        templateType={TEMPLATE.TYPE.TAG}
                        templateMode={TEMPLATE.MODE.ALL}
                        setLoadingState={setLoadingStateTrue}
                        filter={<FilterPopOverHook page="TagList"
                                               templateType={TEMPLATE.TYPE.TAG}
                                               templateMode={TEMPLATE.MODE.ALL}
                                               filterName={TEMPLATE.FILTER.all.tag}
                                               submitValue={dispatchGetTemplateList}
                                               showQualifier={false}
                                               setLoadingState={setLoadingStateTrue} parentName={"TagList"}
                        />}
                    >

                        <TableGridHook
                            id="TagList-table-grid"
                            displayType={"tags"}
                            dataSource={tagList}
                            nameDrawer={openPreview}
                            link={QD_URL +"/search-tag-editor/"}
                            customActions={customActions}
                            pageState={filterState}
                            onPageChange={onPageChange}
                            sortState={filterState}
                            onSortChange={onSortChange}
                            type={metadataTemplateTypes.QuestionTemplate}
                            templateType={TEMPLATE.TYPE.TAG}
                            templateMode={TEMPLATE.MODE.ALL}
                            showQualifier={false}
                            showStatus={false}
                            showVersionCount={false}
                            showGridActionVersions={false}
                            uniqueIdentifier={"id"}
                        />
                    </TableViewHook>

                    <ArgoTemplateListCreateFabHook
                        fileName="TagList"
                        fieldName="fab"
                        hoverText="Create Tag"
                        onClick={openCreate}
                    />

                </CardContent>

            </Card>

        </div>
    );
}

TagListHook.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
};

export default TagListHook;
